import { Injectable, InjectionToken, Injector } from '@angular/core';
import { MSALAuthenticate, ProtectedResource } from './MsalLoader.service';
import { ApiConfigurationService } from '../core/services/configuration.service';
import { ScriptService } from '../core/services/script.service';

// Global Cache sets each of these properties as a string by default.
export type GlobalCacheData = {
  customerId: string;
  customerName: string;
  customerPrimaryDivision: string | undefined;
  customerFilters: AdvancedOneCustomerFilters | undefined;
  startDate: string; //assumes YYYY-MM-DD format
  endDate: string; //assumes YYYY-MM-DD format
};

export type AdvancedOneCustomerFilters = {
  operatingType: string | undefined;
  operatingSubType: string[] | undefined;
  campuses: string[] | undefined;
  customerGroup:
    | {
        levelOne: string | undefined;
        levelTwo: string | undefined;
        levelThree: string | undefined;
        levelFour: string | undefined;
        levelFive: string | undefined;
      }
    | undefined;
  ecolabGroup:
    | {
        region: string | undefined;
        market: string | undefined;
        allocationMarket: string | undefined;
        country: string | undefined;
      }
    | undefined;
  customerIndustry:
    | {
        segmentationIndustryId: string[] | undefined;
        segmentationMarketId: string[] | undefined;
        segmentationSubMarketId: string[] | undefined;
      }
    | undefined;
  sites: string[] | undefined;
};

var globalCacheData: GlobalCacheData | undefined;

export const GLOBAL_CACHE_DATA = new InjectionToken('GlobalCacheDataToken');

function globalCacheDataFactory() {
  return globalCacheData;
}

export type GlobalCacheConfig = {
  scriptUrl: string;
  apiResource: ProtectedResource;
};

var globalCacheConfig: GlobalCacheConfig;

@Injectable({
  providedIn: 'root',
})
export class GlobalCacheLoaderService {
  constructor(private injector: Injector) {}

  async loadGlobalCacheConfig() {
    const apiConfigService = this.injector.get(ApiConfigurationService);
    const bootstrap = apiConfigService.getBootstrapConfig();
    const appConfig = apiConfigService.getAppConfig();

    globalCacheConfig = {
      scriptUrl: bootstrap.gcScriptUrl,
      apiResource: bootstrap.globalCacheApiResource
    };
  }

  async loadGlobalCacheScript() {
    if (globalCacheConfig.scriptUrl) {
      const scriptService = this.injector.get(ScriptService);
      // const scriptService = new ScriptService();
      await scriptService.loadScript(
        'globalCacheScriptUrl',
        globalCacheConfig.scriptUrl,
      );
    }
    try {
    } catch (error) {
      // Ignore any error for now until it is clear how to handle failures in loading the global cache script
      console.error('Error fetching global cache configuration:', error);
    }
  }

  async loadGlobalCacheData() {
    if (typeof getFromUserSession !== 'function') {
      console.error(
        'Unable to get session data. The getFromUserSession function is not defined. Redirecting to the filter page.',
      );
      return;
    }

    const token = await MSALAuthenticate(
      this.injector,
      globalCacheConfig.apiResource.scopes,
    );
    if (!token) {
      console.error(
        'Failed to get session data because of failure to acquire an access token.',
      );
      return;
    }

    try {
      const response = await getFromUserSession(token);
      const text = await response.text();
      let globalCacheJson: any = JSON.parse(text);

      // Parse the JSON string to an object if the parsed text is still a string. This may happen if the content
      // type is not set correctly in the response, i.e. it is set to text/plain instead of application/json.
      // Instead of checking the content type, which can be anything not within our control, check if the parsed
      // string is stil a string.
      if (typeof globalCacheJson == 'string') {
        globalCacheJson = JSON.parse(globalCacheJson);
      }

      // If the customerFilters property is still a JSON string, parse it to the AdvancedOneCustomerFilters type.
      let customerFilters = globalCacheJson?.customerFilters;
      if (customerFilters != undefined && typeof customerFilters == 'string') {
        globalCacheJson.customerFilters = JSON.parse(customerFilters);
      }

      globalCacheData = globalCacheJson as GlobalCacheData;
    } catch (error) {
      console.error('Error retrieving data from getFromUserSession:', error);
    }
  }
}

export const GLOBAL_CACHE_PROVIDERS = [
  {
    provide: GLOBAL_CACHE_DATA,
    useFactory: globalCacheDataFactory,
  },
];
